import React, { useState, useEffect } from 'react';
import './Projects.css';
import { ExternalLink, Github, X, CheckCircle, ArrowLeft } from 'lucide-react';

// Default projects that always show
const defaultProjects = [
    {
      id: 'rawad-barbershop',
      title: 'RA BARBER',
      description: 'Professional barber shop mobile application for Rawad Barbershop. A modern app designed to help customers book appointments and connect with professional barbering services.',
      technologies: ['iOS', 'Mobile App', 'App Store'],
      imageUrl: '/graphicrawad.png',
      liveUrl: 'https://apps.apple.com/il/app/ra-barber/id6752573773',
      githubUrl: '',
      featured: true,
      order: 1,
      fullDescription: 'RA BARBER is a professional mobile application designed for Rawad Barbershop, providing customers with a seamless way to book appointments and connect with professional barbering services. The app offers an intuitive interface that makes scheduling appointments quick and easy.',
      features: [
        'Appointment booking system',
        'Service selection and customization',
        'Barber profiles and availability',
        'Push notifications for reminders',
        'User-friendly interface',
        'App Store integration'
      ],
      highlights: [
        'Native iOS application',
        'Professional barber shop management',
        'Seamless booking experience',
        'Modern UI/UX design'
      ],
      purpose: 'RA BARBER was created to modernize the appointment booking process for barber shops, making it easier for customers to schedule services and for businesses to manage their appointments efficiently.'
    },
    {
      id: 'loopify-b2b',
      title: 'Loopify',
      description: 'B2B platform solution designed to streamline business operations and enhance connectivity between businesses. A comprehensive system for managing business-to-business relationships and workflows.',
      technologies: ['B2B', 'Platform', 'Web Application'],
      imageUrl: '/loopifyLogo.png',
      liveUrl: 'https://loopify-d37c9.web.app/dashboard',
      githubUrl: '',
      featured: true,
      order: 2,
      fullDescription: 'Loopify is a comprehensive B2B platform solution designed to streamline business operations and enhance connectivity between businesses. The platform provides tools for managing business-to-business relationships, workflows, and operations in a centralized, efficient system.',
      features: [
        'Business relationship management',
        'Workflow automation',
        'Dashboard analytics',
        'Multi-business connectivity',
        'Secure data management',
        'Real-time collaboration tools'
      ],
      highlights: [
        'Comprehensive B2B solution',
        'Streamlined business operations',
        'Enhanced inter-business connectivity',
        'Modern web platform'
      ],
      purpose: 'Loopify was developed to solve the challenges businesses face when managing multiple B2B relationships, providing a centralized platform that enhances efficiency and connectivity.'
    },
    {
      id: 'studiax',
      title: 'Studiax',
      description: 'AI-powered cross-platform study management platform helping students organize their academic life. Features AI study assistant, task management, deadline tracking, and study planning tools. Available on iOS, Android, Web, and macOS.',
      technologies: ['iOS', 'Android', 'React Native', 'AI', 'Firebase', 'Node.js'],
      imageUrl: '/studiaxIcon.png',
      liveUrl: 'https://apps.apple.com/il/app/studiax/id6757106680',
      githubUrl: '',
      featured: true,
      order: 3,
      fullDescription: 'Studiax is an AI-powered cross-platform study management application designed to help students organize their academic life and study more effectively using AI assistance. The app combines task management, scheduling, and an AI study assistant into a single, intuitive experience.',
      features: [
        'AI Study Assistant - Chat-based assistant for explanations, summaries, and study tips',
        'Task & Deadline Management - Create tasks with deadlines and automatic reminder notifications',
        'Study Planning Tools - Schedule courses and daily study sessions, track progress across semesters',
        'Cross-Platform Support - Available on Web, iOS, Android, and macOS',
        'Authentication & User Accounts - Secure user authentication with personalized data per user',
        'Clean, Mobile-First UI - Modern, responsive interface optimized for all devices'
      ],
      highlights: [
        'Implemented secure AI access through a backend proxy to protect API keys',
        'Designed persistent AI chat history with context continuation',
        'Built a mobile-first system that adapts correctly to desktop environments',
        'Applied best practices for deployment, security, and user experience'
      ],
      techStack: {
        frontend: 'React Native (Expo), React Native Web',
        backend: 'Node.js, Express',
        ai: 'OpenAI API (via secure backend)',
        auth: 'Firebase Authentication',
        notifications: 'Local device notifications',
        hosting: 'Backend hosted on Render (HTTPS), Frontend deployed as web application'
      },
      purpose: 'Studiax was created to explore how AI can enhance student productivity, not by replacing learning, but by supporting planning, understanding, and consistency.'
    },
    {
      id: 'ameer-barber',
      title: 'Ameer Barber',
      description: 'Barber shop booking application that lets customers create accounts, log in, and book appointments while giving the barber/admin tools to manage availability and bookings.',
      technologies: ['iOS', 'Android', 'Firebase', 'Booking App'],
      imageUrl: '/ameerBarberLogo.png',
      liveUrl: '',
      supportUrl: '/ameer-barber/support',
      privacyUrl: '/ameer-barber/privacy',
      githubUrl: '',
      featured: true,
      order: 4,
      fullDescription: 'Ameer Barber is a barber shop booking app built to simplify appointment management for customers and barbers. Users can sign in, select services, and schedule appointments, while the admin can manage time slots, bookings, and customer flow in one place.',
      features: [
        'Account creation and secure login',
        'Appointment booking with date and time selection',
        'Service selection with booking notes',
        'Admin availability and booking management',
        'Booking confirmations and customer updates',
        'Mobile-friendly, easy-to-use interface'
      ],
      highlights: [
        'Customer-first booking flow designed for speed',
        'Admin workflow focused on daily schedule control',
        'Built to support reliable barber shop operations'
      ],
      purpose: 'Ameer Barber was created to help customers book appointments more easily and help the barber manage schedules efficiently.'
    }
];

// Project Modal Component
const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div className="project-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="project-modal-close" onClick={onClose} aria-label="Close modal">
          <X size={24} />
        </button>
        <button className="project-modal-back" onClick={onClose} aria-label="Go back">
          <ArrowLeft size={24} />
        </button>
        
        {project.imageUrl && (
          <div className="project-modal-image">
            <img src={project.imageUrl} alt={project.title} />
          </div>
        )}

        <div className="project-modal-body">
          <h2 className="project-modal-title">{project.title}</h2>
          
          <p className="project-modal-description">
            {project.fullDescription || project.description}
          </p>

          {project.features && project.features.length > 0 && (
            <div className="project-modal-section">
              <h3 className="project-modal-section-title">Key Features</h3>
              <ul className="project-modal-features">
                {project.features.map((feature, index) => (
                  <li key={index}>
                    <CheckCircle size={18} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.highlights && project.highlights.length > 0 && (
            <div className="project-modal-section">
              <h3 className="project-modal-section-title">Project Highlights</h3>
              <ul className="project-modal-highlights">
                {project.highlights.map((highlight, index) => (
                  <li key={index}>{highlight}</li>
                ))}
              </ul>
            </div>
          )}

          {project.techStack && (
            <div className="project-modal-section">
              <h3 className="project-modal-section-title">Technical Stack</h3>
              <div className="project-modal-tech-stack">
                {Object.entries(project.techStack).map(([key, value]) => (
                  <div key={key} className="tech-stack-item">
                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.technologies && project.technologies.length > 0 && (
            <div className="project-modal-section">
              <h3 className="project-modal-section-title">Technologies</h3>
              <div className="project-modal-technologies">
                {project.technologies.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>
          )}

          {project.purpose && (
            <div className="project-modal-section">
              <h3 className="project-modal-section-title">Purpose</h3>
              <p className="project-modal-purpose">{project.purpose}</p>
            </div>
          )}

          <div className="project-modal-actions">
            {project.liveUrl && (
              <a 
                href={project.liveUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="project-modal-button primary"
              >
                <ExternalLink size={18} />
                View Live Project
              </a>
            )}
            {project.supportUrl && (
              <a
                href={project.supportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="project-modal-button secondary"
              >
                <ExternalLink size={18} />
                Support
              </a>
            )}
            {project.privacyUrl && (
              <a
                href={project.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="project-modal-button tertiary"
              >
                <ExternalLink size={18} />
                Privacy Policy
              </a>
            )}
            {project.githubUrl && (
              <a 
                href={project.githubUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="project-modal-button secondary"
              >
                <Github size={18} />
                View on GitHub
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  // Start with default projects immediately - no async operations blocking render
  const [projects, setProjects] = useState(defaultProjects);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Defer Firebase fetch to after page load - completely non-blocking
    const fetchFirebaseProjects = async () => {
      // Wait for page to be fully loaded and idle before attempting Firebase fetch
      if (window.requestIdleCallback) {
        window.requestIdleCallback(async () => {
          await tryFetchFirebaseProjects();
        }, { timeout: 5000 });
      } else {
        // Fallback for browsers without requestIdleCallback - wait 2 seconds
        setTimeout(async () => {
          await tryFetchFirebaseProjects();
        }, 2000);
      }
    };

    const tryFetchFirebaseProjects = async () => {
      try {
        // Dynamically import Firebase only when needed (lazy loading)
        const { collection, getDocs } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');
        
        const projectsCollection = collection(db, 'projects');
        const projectsSnapshot = await getDocs(projectsCollection);
        
        const firebaseProjects = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        if (firebaseProjects.length > 0) {
          // Merge default projects with Firebase projects
          const allProjects = [...defaultProjects, ...firebaseProjects];
          
          // Sort by order if available
          allProjects.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0;
          });
          
          // Update projects state (non-blocking update)
          setProjects(allProjects);
        }
      } catch (err) {
        // Silently fail - default projects are already shown
        // No error handling needed - projects already visible
      }
    };

    // Start fetch after component mounts (completely non-blocking)
    fetchFirebaseProjects();
  }, []);

  return (
    <section id="projects" className="projects">
      <div className="projects-container">
        <div className="projects-header">
          <h2 className="section-title">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="section-description">
            A collection of innovative projects showcasing technical expertise and creative solutions
          </p>
        </div>

        <div className="projects-grid">
          {projects.map((project) => (
            <div 
              key={project.id} 
              className={`project-card ${project.featured ? 'featured' : ''}`}
              onClick={() => {
                setSelectedProject(project);
                setIsModalOpen(true);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedProject(project);
                  setIsModalOpen(true);
                }
              }}
            >
              {project.imageUrl && (
                <div className="project-image">
                  <img src={project.imageUrl} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-links">
                      <button 
                        className="project-link view-details"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProject(project);
                          setIsModalOpen(true);
                        }}
                        aria-label={`View details for ${project.title}`}
                      >
                        View Details
                      </button>
                      {project.liveUrl && (
                        <a 
                          href={project.liveUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="project-link"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Visit ${project.title}`}
                        >
                          <ExternalLink size={20} />
                        </a>
                      )}
                      {project.githubUrl && (
                        <a 
                          href={project.githubUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="project-link"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`View ${project.title} on GitHub`}
                        >
                          <Github size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                
                {project.technologies && project.technologies.length > 0 && (
                  <div className="project-technologies">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                )}
                <div className="project-click-hint">Click to view details →</div>
              </div>
            </div>
          ))}
        </div>

        <ProjectModal 
          project={selectedProject} 
          isOpen={isModalOpen} 
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProject(null);
          }} 
        />

        {projects.length === 0 && (
          <div className="empty-state">
            <p>No projects found. Add projects to your Firebase Firestore collection to get started.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;



